import React from 'react';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import useScrollAnimation from '../hooks/useScrollAnimation';
import './CaseStudies.css';

const caseStudiesData = [
  {
    title: 'AI-Powered VoIP Call Center Automation',
    client: 'Major Telecom Provider',
    industry: 'Telecommunications',
    description: 'Deployed intelligent VoIP telephony platform with Agentic AI voice bots and LLM-powered conversation handling, reducing call processing time by 60% while improving customer satisfaction by 45%.',
    results: ['60% faster call resolution', '45% higher satisfaction', '40% cost reduction'],
    technologies: ['Agentic AI', 'LLM', 'VoIP Platform']
  },
  {
    title: 'LiDAR-Based Skin Anomaly Detection System',
    client: 'Dermatology Research Institute',
    industry: 'Healthcare',
    description: 'Developed advanced LiDAR and machine learning system for early detection of skin anomalies and lesions. 3D depth mapping combined with ML models achieved 94% accuracy in identifying potential concerns requiring medical attention.',
    results: ['94% detection accuracy', '70% faster screening', 'Early detection rates improved'],
    technologies: ['LiDAR Technology', 'Machine Learning', 'Computer Vision']
  },
  {
    title: 'IoT Sensor-Based Health Monitoring Platform',
    client: 'Senior Care Network',
    industry: 'Healthcare',
    description: 'Implemented body-worn sensor system with real-time vital signs monitoring for 5,000+ senior citizens. AI-powered anomaly detection reduced emergency incidents by 35% through proactive health alerts and caregiver notifications.',
    results: ['35% fewer emergencies', '5,000+ users', '24/7 AI monitoring'],
    technologies: ['IoT Sensors', 'Machine Learning', 'Real-time Analytics']
  },
  {
    title: 'Retail Digital Transformation Platform',
    client: 'National Retail Chain',
    industry: 'Retail',
    description: 'Built cloud-native e-commerce platform with ML-powered recommendations, integrated POS, inventory management, and CRM. Increased online sales by 150% in first year with 99.9% uptime.',
    results: ['150% sales growth', '99.9% uptime', '200+ stores connected'],
    technologies: ['Cloud Platform', 'Machine Learning', 'Enterprise Software']
  },
  {
    title: 'Smart Valet Parking Management',
    client: 'Premium Hotel Chain',
    industry: 'Hospitality',
    description: 'Developed IoT-enabled valet parking platform with real-time vehicle tracking, automated staff coordination, and predictive analytics. Reduced wait times by 70% and improved customer experience ratings.',
    results: ['70% faster service', '95% customer satisfaction', '50+ locations'],
    technologies: ['IoT', 'Edge Computing', 'Mobile Platform']
  },
  {
    title: 'Conversational AI for Enterprise Support',
    client: 'Global SaaS Company',
    industry: 'Technology',
    description: 'Built custom SLM-powered conversational AI system for customer support. Multi-agent AI handles 80% of inquiries automatically, with seamless human handoff for complex cases.',
    results: ['80% automated resolution', '3x faster response time', '92% accuracy'],
    technologies: ['SLM', 'Agentic AI', 'NLP']
  },
  {
    title: 'Predictive Maintenance with Computer Vision',
    client: 'Manufacturing Enterprise',
    industry: 'Manufacturing',
    description: 'Deployed machine learning and computer vision system for predictive equipment maintenance. AI models analyze sensor data and visual inspections to predict failures 2 weeks in advance.',
    results: ['85% downtime reduction', '2-week early detection', '$2M cost savings'],
    technologies: ['Machine Learning', 'Computer Vision', 'Edge AI']
  }
];

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.97 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const CaseStudies = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="case-studies" className="case-studies-section">
      <div className="case-studies-container">
        <motion.div
          ref={headerRef}
          className="case-studies-header"
          variants={sectionVariants}
          initial="hidden"
          animate={headerVisible ? 'visible' : 'hidden'}
        >
          <div className="section-label">Our Work</div>
          <h2 className="display-large">Projects We've <span className="gradient-text">Delivered</span></h2>
          <p className="body-medium">Real results from real clients across diverse industries</p>
        </motion.div>

        <div ref={gridRef} className="case-studies-grid">
          {caseStudiesData.map((study, index) => (
            <motion.div
              key={index}
              className="case-study-card dark-hover"
              custom={index}
              variants={cardVariants}
              initial="hidden"
              animate={gridVisible ? 'visible' : 'hidden'}
              whileHover={{ y: -6, boxShadow: '0 12px 40px rgba(0, 255, 209, 0.2)' }}
              transition={{ duration: 0.25 }}
            >
              <div className="case-study-badge">{study.industry}</div>
              <h3 className="heading-2">{study.title}</h3>
              <div className="case-study-client body-muted">{study.client}</div>
              <p className="body-small case-study-description">{study.description}</p>
              <div className="case-study-technologies">
                {study.technologies.map((tech, idx) => (
                  <span key={idx} className="tech-tag">{tech}</span>
                ))}
              </div>
              <div className="case-study-results">
                <h4 className="heading-3">Key Results:</h4>
                <ul className="results-list">
                  {study.results.map((result, idx) => (
                    <li key={idx} className="body-small">{result}</li>
                  ))}
                </ul>
              </div>
              <motion.button
                className="btn-secondary learn-more-btn"
                whileHover={{ scale: 1.04, x: 4 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}
              >
                Learn More
                <ArrowRight size={18} />
              </motion.button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;